import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageSquareWarning, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const API_URL = process.env.REACT_APP_API_URL;

const COPY = {
  tr: {
    validation: 'Lütfen konu ve yaşadığınız sorunu biraz daha ayrıntılı yazın.',
    techInfo: 'Otomatik teknik bilgi',
    page: 'Sayfa',
    time: 'Zaman',
    browser: 'Tarayıcı',
    success: 'Sorun bildiriminiz bize ulaştı. Teşekkürler.',
    failed: 'Sorun bildirimi gönderilemedi.',
    button: 'Sorun / Öneri Bildir',
    title: 'Sorun / Öneri Bildir',
    intro: 'Yaşadığınız sorunu yazın. Bulunduğunuz sayfa bilgisi otomatik eklenir.',
    close: 'Kapat',
    category: 'Kategori',
    categories: {
      bug: 'Hata / sorun',
      feature: 'Özellik önerisi',
      billing: 'Ödeme / abonelik',
      other: 'Diğer',
    },
    subject: 'Konu',
    subjectPlaceholder: 'Örn. PDF önizlemede görsel çıkmıyor',
    message: 'Ne oldu?',
    messagePlaceholder: 'Ne yaparken sorun yaşadığınızı ve ekranda ne gördüğünüzü yazın.',
    cancel: 'İptal',
    send: 'Gönder',
    sending: 'Gönderiliyor…',
  },
  en: {
    validation: 'Please describe the subject and issue in a little more detail.',
    techInfo: 'Automatic technical information',
    page: 'Page',
    time: 'Time',
    browser: 'Browser',
    success: 'Your report has been sent. Thank you.',
    failed: 'Could not send your report.',
    button: 'Report Issue / Suggestion',
    title: 'Report Issue / Suggestion',
    intro: 'Describe what happened. The current page is added automatically.',
    close: 'Close',
    category: 'Category',
    categories: {
      bug: 'Bug / issue',
      feature: 'Feature suggestion',
      billing: 'Payment / subscription',
      other: 'Other',
    },
    subject: 'Subject',
    subjectPlaceholder: 'e.g. Product image is missing in PDF preview',
    message: 'What happened?',
    messagePlaceholder: 'Describe what you were doing and what you saw on screen.',
    cancel: 'Cancel',
    send: 'Send',
    sending: 'Sending…',
  },
};

export function QuoteSupportButton({ open: controlledOpen, onOpenChange }) {
  const location = useLocation();
  const { i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || i18n.language || 'tr').split('-')[0];
  const text = COPY[lang === 'tr' ? 'tr' : 'en'];
  const [internalOpen, setInternalOpen] = useState(false);
  const controlled = typeof controlledOpen === 'boolean';
  const open = controlled ? controlledOpen : internalOpen;
  const setOpen = (value) => {
    if (controlled) onOpenChange?.(value);
    else setInternalOpen(value);
  };
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [category, setCategory] = useState('bug');
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (subject.trim().length < 3 || message.trim().length < 10) {
      toast.error(text.validation);
      return;
    }
    setSending(true);
    try {
      const context = [
        message.trim(),
        '',
        `--- ${text.techInfo} ---`,
        `${text.page}: ${location.pathname}${location.search || ''}`,
        `${text.time}: ${new Date().toISOString()}`,
        `${text.browser}: ${navigator.userAgent}`,
      ].join('\n');
      await axios.post(`${API_URL}/api/support/tickets`, {
        subject: subject.trim(),
        message: context,
        category,
      });
      toast.success(text.success);
      setSubject('');
      setMessage('');
      setCategory('bug');
      setOpen(false);
    } catch (err) {
      toast.error(lang === 'tr' ? (err.response?.data?.detail || text.failed) : text.failed);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed right-4 bottom-20 lg:bottom-5 z-[60] rounded-full shadow-lg gap-2 bg-slate-900 hover:bg-slate-800"
        data-testid="quote-report-issue-button"
      >
        <MessageSquareWarning className="w-4 h-4" />
        {text.button}
      </Button>

      {open && (
        <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4" data-testid="quote-report-issue-modal">
          <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl border border-[#E5E5E5] p-5">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h2 className="text-lg font-semibold text-[#1A1A2E]">{text.title}</h2>
                <p className="text-xs text-[#64748B] mt-1">{text.intro}</p>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="p-1 text-[#64748B]" aria-label={text.close}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <Label>{text.category}</Label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="h-10 w-full border border-[#E5E5E5] rounded-md px-3 text-sm bg-white"
                >
                  <option value="bug">{text.categories.bug}</option>
                  <option value="feature">{text.categories.feature}</option>
                  <option value="billing">{text.categories.billing}</option>
                  <option value="other">{text.categories.other}</option>
                </select>
              </div>
              <div>
                <Label>{text.subject}</Label>
                <Input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200} placeholder={text.subjectPlaceholder} />
              </div>
              <div>
                <Label>{text.message}</Label>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={6}
                  maxLength={4500}
                  placeholder={text.messagePlaceholder}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" type="button" onClick={() => setOpen(false)} disabled={sending}>{text.cancel}</Button>
                <Button type="button" onClick={submit} disabled={sending}>
                  {sending ? text.sending : text.send}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default QuoteSupportButton;
